import React from "react";
import Header from "components/Header";

import {
	Chart as ChartJS,
	BarElement,
	LineElement,
	PointElement,
	ArcElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
} from "chart.js";

import { Line, Bar, Doughnut } from "react-chartjs-2";

/* ------------------------------------------------------------------ */
/* Chart.js Registration                                              */
/* ------------------------------------------------------------------ */
ChartJS.register(
	BarElement,
	LineElement,
	PointElement,
	ArcElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend
);

/* ------------------------------------------------------------------ */
/* Section 1 – TB KPIs                                                 */
/* ------------------------------------------------------------------ */
const tbKpis = [
	{
		title: "TB Notification Rate",
		value: 168,
		unit: "/1L",
		change: 3,
		trendData: [150, 154, 158, 160, 165, 168],
	},
	{
		title: "Private Sector Notification",
		value: 28,
		unit: "%",
		change: 2,
		trendData: [22, 24, 25, 26, 27, 28],
	},
	{
		title: "Pediatric TB Proportion",
		value: 6.4,
		unit: "%",
		change: -1,
		trendData: [6.9, 6.8, 6.7, 6.6, 6.5, 6.4],
	},
	{
		title: "Treatment Initiation Rate",
		value: 91,
		unit: "%",
		change: -1,
		trendData: [93, 92.5, 92, 91.5, 91, 91],
	},
	{
		title: "Treatment Success Rate",
		value: 83,
		unit: "%",
		change: -1,
		trendData: [85, 84.5, 84, 83.5, 83, 85],
	},
	{
		title: "Death Rate",
		value: 3.6,
		unit: "%",
		change: 1,
		trendData: [3.5, 3.3, 3.4, 3.5, 3.6, 3.8],
	},
];

/* ------------------------------------------------------------------ */
/* Section 2 – Care Cascade & Risk KPIs                                */
/* ------------------------------------------------------------------ */
const tbCascadeData = {
	labels: [
		"Notified",
		"Diagnosed",
		"Treatment Initiated",
		"Treatment Completed",
	],
	values: [100, 86, 78, 65],
};
const vbdCaseData = {
	labels: ["Dengue", "Malaria", "Filaria", "Chikungunya"],
	values: [12450, 3820, 1460, 980],
};

const vbdCfrTrend = {
	labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
	values: [0.32, 0.29, 0.27, 0.25, 0.26, 0.29],
};

const ltfuKpi = {
	title: "Lost to Follow-up Rate",
	value: 4.1,
	unit: "%",
	change: -3,
	trendData: [5.0, 4.8, 4.6, 4.4, 4.2, 4.1],
};

const mdrKpi = {
	title: "MDR-TB Detection Rate",
	value: 72,
	unit: "%",
	change: 2,
	trendData: [66, 68, 69, 70, 71, 72],
};
const tbOutcomeData = {
	labels: ["Current Cohort"],
	success: 83,
	ltfu: 4.1,
	death: 3.6,
	notEvaluated: 9.3,
};
const drtbStatusData = {
	labels: ["On Treatment", "Not Initiated", "Completed"],
	values: [64, 21, 15],
};

const adherenceMethodMix = {
	labels: ["99DOTS", "MERm", "Video DOT", "Manual"],
	values: [42, 18, 12, 28],
};

const stockOutReasonSplit = {
	labels: ["Procurement Delay", "Distribution Delay", "Forecasting Error"],
	values: [46, 34, 20],
};

const treatmentDelayTrend = {
	labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
	actual: [7.2, 6.8, 6.5, 6.3, 6.1, 6.0],
	target: [5, 5, 5, 5, 5, 5],
};
const preventiveCoverageData = {
	labels: [
		"Preventive Therapy Completion",
		"TB-HIV Testing Coverage",
		"HIV+ TB on ART",
		"TB-Diabetes Screening",
	],
	values: [62, 88, 81, 74],
	targets: [90, 95, 95, 90],
};
const dbtData = {
	labels: ["Beneficiary Coverage", "Payment Timeliness"],
	actual: [82, 68],
	target: [100, 90],
};

const nikshayMitraData = {
	covered: 41,
	gap: 59,
};

const tbDiabetesBurden = {
	label: "TB Patients with Diabetes",
	value: 18, // %
};
const hivData = {
	prevalence: 0.31, // %
	artCoverage: 86, // %
};

const hepatitisTrend = {
	labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
	cases: [420, 390, 445, 470, 455, 480],
};
const StatusBadge = ({ status }) => {
	const styles = {
		green: "bg-green-100 text-green-700",
		amber: "bg-amber-100 text-amber-700",
		red: "bg-rose-100 text-rose-700",
	};

	const labels = {
		green: "On Track",
		amber: "Needs Attention",
		red: "High Risk",
	};

	return (
		<span
			className={`text-xs font-medium px-2.5 py-1 rounded-full ${styles[status]}`}
		>
			{labels[status]}
		</span>
	);
};
const PriorityActions = () => (
	<div className="bg-white rounded-xl shadow-sm p-4 h-full">
	  <h3 className="text-sm font-medium text-gray-700 mb-3">
		Priority Actions (Next 90 Days)
	  </h3>
  
	  <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
		<li>
		  Fast-track DR-TB treatment initiation through weekly district reviews.
		</li>
		<li>
		  Improve DBT payment timeliness via bank reconciliation.
		</li>
		<li>
		  Expand ICT-enabled adherence tools in high LTFU districts.
		</li>
		<li>
		  Strengthen TB–Diabetes screening and referral mechanisms.
		</li>
		<li>
		  Pre-position dengue and malaria logistics ahead of peak season.
		</li>
	  </ol>
	</div>
  );
  
const ProgramScorecard = () => {
	const rows = [
		{
			area: "TB Case Detection",
			status: "green",
			note: "Notification rate improving",
		},
		{
			area: "Treatment Outcomes",
			status: "amber",
			note: "TSR plateaued; LTFU persists",
		},
		{
			area: "DR-TB Management",
			status: "red",
			note: "Delayed treatment initiation",
		},
		{
			area: "Preventive & Comorbidity Care",
			status: "amber",
			note: "Low preventive therapy completion",
		},
		{
			area: "Patient Support (DBT & Mitra)",
			status: "red",
			note: "Payment delays and low coverage",
		},
	];

	return (
		<div className="bg-white rounded-xl shadow-sm p-4">
			<h3 className="text-sm font-medium text-gray-700 mb-4">
				Program Performance Scorecard
			</h3>

			<div className="space-y-3">
				{rows.map((row, idx) => (
					<div
						key={idx}
						className="grid grid-cols-[2.5fr_1.5fr_3fr] items-center text-sm"
					>
						<span className="text-gray-800 font-medium">
							{row.area}
						</span>

						<StatusBadge status={row.status} />

						<span className="text-gray-500">
							{row.note}
						</span>
					</div>
				))}
			</div>
		</div>
	);
};
const OverallStatusBanner = () => (
	<div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
		<div className="flex items-center gap-2">
			<StatusBadge status="amber" />
			<h3 className="text-lg font-semibold text-amber-800">
				Overall Program Status
			</h3>
		</div>

		<p className="text-sm text-amber-700 mt-2">
			Multiple program areas require corrective action in the next quarter.
		</p>
	</div>
);

/* ------------------------------------------------------------------ */
/* KPI Card Component                                                  */
/* ------------------------------------------------------------------ */


const HepatitisTrendChart = () => (
	<div className="bg-white rounded-xl shadow-sm p-4">
		<h3 className="text-sm font-medium text-gray-700 mb-3">
			Viral Hepatitis Cases Reported
		</h3>

		<div className="h-[240px]">
			<Line
				data={{
					labels: hepatitisTrend.labels,
					datasets: [
						{
							label: "Cases",
							data: hepatitisTrend.cases,
							borderColor: "#6366f1",
							backgroundColor: "rgba(99,102,241,0.15)",
							tension: 0.4,
							fill: true,
							pointRadius: 3,
						},
					],
				}}
				options={{
					responsive: true,
					maintainAspectRatio: false,
					plugins: {
						legend: { display: false },
						tooltip: {
							callbacks: {
								label: (ctx) =>
									`${ctx.parsed.y.toLocaleString('en-IN')} cases`,
							},
						},
						datalabels: false
					},
					scales: {
						y: {
							ticks: {
								callback: (v) => v.toLocaleString('en-IN'),
							},
							grid: { color: "#f1f5f9" },
						},
						x: {
							grid: { display: false },
						},
					},
				}}
			/>
		</div>
	</div>
);

const HivKpiCard = ({ title, value, unit }) => (
	<div className="bg-white rounded-xl shadow-sm p-4 mt-2">
		<p className="text-sm text-gray-500">{title}</p>

		<h3 className="text-3xl font-semibold text-gray-900 mt-2">
			{value}
			<span className="text-base font-medium text-gray-500 ml-1">
				{unit}
			</span>
		</h3>

		<p className="text-xs text-gray-400 mt-1">
			Current reporting period
		</p>
	</div>
);

const VbdCfrTrendChart = () => (
	<div className="bg-white rounded-xl shadow-sm p-4">
		<h3 className="text-sm font-medium text-gray-700 mb-3">
			Case Fatality Rate Trend (%)
		</h3>

		<div className="h-[220px]">
			<Line
				data={{
					labels: vbdCfrTrend.labels,
					datasets: [
						{
							label: "CFR",
							data: vbdCfrTrend.values,
							borderColor: "#f43f5e",
							backgroundColor: "rgba(244,63,94,0.15)",
							tension: 0.4,
							fill: true,
							pointRadius: 3,
						},
					],
				}}
				options={{
					responsive: true,
					maintainAspectRatio: false,
					plugins: {
						legend: { display: false },
						tooltip: {
							callbacks: {
								label: (ctx) => `${ctx.parsed.y}%`,
							},
						},
						datalabels: false,
					},
					scales: {
						y: {
							ticks: {
								callback: (v) => `${v}%`,
							},
							grid: { color: "#f1f5f9" },
						},
						x: {
							grid: { display: false },
						},
					},
					datalabels: false
				}}
			/>
		</div>
	</div>
);
const VbdCaseBurdenChart = () => (
	<div className="bg-white rounded-xl shadow-sm p-4">
		<h3 className="text-sm font-medium text-gray-700 mb-3">
			Vector-borne Disease Cases Reported
		</h3>

		<div className="h-[260px]">
			<Bar
				data={{
					labels: vbdCaseData.labels,
					datasets: [
						{
							label: "Cases",
							data: vbdCaseData.values,
							backgroundColor: [
								"#6366f1",
								"#22c55e",
								"#f97316",
								"#60a5fa",
							],
							borderRadius: 6,
							barThickness: 32,
						},
					],
				}}
				options={{
					responsive: true,
					maintainAspectRatio: false,
					plugins: {
						legend: { display: false },
						tooltip: {
							callbacks: {
								label: (ctx) =>
									`${ctx.parsed.y.toLocaleString('en-IN')} cases`,
							},
						},
						datalabels: false
					},
					scales: {
						y: {
							ticks: {
								callback: (v) => v.toLocaleString('en-IN'),
							},
							grid: { color: "#f1f5f9" },
						},
						x: {
							grid: { display: false },
						},
					},
				}}
			/>
		</div>
	</div>
);
const DbtSupportChart = () => {
	return (
		<div className="bg-white rounded-xl shadow-sm p-4">
			<h3 className="text-sm font-medium text-gray-700 mb-3">
				Direct Benefit Transfer (DBT) Performance (%)
			</h3>

			<Bar
				data={{
					labels: dbtData.labels,
					datasets: [
						{
							label: "Actual",
							data: dbtData.actual,
							backgroundColor: "#6366f1",
							barThickness: 24,
						},
						{
							label: "Target",
							data: dbtData.target,
							backgroundColor: "#e5e7eb",
							barThickness: 24,
						},
					],
				}}
				options={{
					plugins: {
						legend: { position: "bottom" },
						tooltip: {
							callbacks: {
								label: (ctx) => `${ctx.parsed.y}%`,
							},
						},
						datalabels: false,
					},
					scales: {
						y: {
							max: 100,
							ticks: {
								callback: (v) => `${v}%`,
							},
							grid: { color: "#f1f5f9" },
						},
						x: {
							grid: { display: false },
						},
					},
				}}
			/>
		</div>
	);
};
const NikshayMitraCoverage = () => {
	return (
		<div className="bg-white rounded-xl shadow-sm p-4">
			<h3 className="text-sm font-medium text-gray-700 mb-3">
				Community Support (Nikshay Mitra)
			</h3>

			<Bar
				data={{
					labels: ["TB Patients"],
					datasets: [
						{
							label: "Supported",
							data: [nikshayMitraData.covered],
							backgroundColor: "#34d399",
						},
						{
							label: "Support Gap",
							data: [nikshayMitraData.gap],
							backgroundColor: "#e5e7eb",
						},
					],
				}}
				options={{
					indexAxis: "y",
					plugins: {
						legend: { position: "bottom" },
						tooltip: {
							callbacks: {
								label: (ctx) => `${ctx.parsed.x}%`,
							},
						},
						datalabels: false,
					},
					scales: {
						x: {
							stacked: true,
							max: 100,
							ticks: {
								callback: (v) => `${v}%`,
							},
							grid: { color: "#f1f5f9" },
						},
						y: {
							stacked: true,
							grid: { display: false },
						},
					},
				}}
			/>
		</div>
	);
};
const DrtbTreatmentStatusBar = () => (
	<div className="bg-white rounded-xl shadow-sm p-4 ">
		<h3 className="text-sm font-medium text-gray-700 mb-3">
			DR-TB Treatment Status (%)
		</h3>

		<div className="">
			<Bar
				data={{
					labels: ["DR-TB Patients"],
					datasets: [
						{
							label: "On Treatment",
							data: [64],
							backgroundColor: "#22c55e",
						},
						{
							label: "Not Initiated",
							data: [21],
							backgroundColor: "#f97316",
						},
						{
							label: "Completed",
							data: [15],
							backgroundColor: "#6366f1",
						},
					],
				}}
				options={{
					indexAxis: "y",
					scales: {
						x: {
							stacked: true,
							max: 100,
							ticks: { callback: (v) => `${v}%` },
						},
						y: { stacked: true },
					},
					plugins: {
						legend: { position: "bottom" },
						tooltip: {
							callbacks: {
								label: (ctx) => `${ctx.label}: ${ctx.parsed.x}%`,
							},
						},
						datalabels: false,
					},
				}}
			/>
		</div>
	</div>
);
const AdherenceMethodMixBar = () => (
	<div className="bg-white rounded-xl shadow-sm p-4 ">
		<h3 className="text-sm font-medium text-gray-700 mb-3">
			Adherence Method Mix (%)
		</h3>

		<div className="">
			<Bar
				data={{
					labels: ["99DOTS", "MERm", "Video DOT", "Manual"],
					datasets: [
						{
							label: "Coverage",
							data: [42, 18, 12, 28],
							backgroundColor: "#6366f1",
							barThickness: 18,
						},
					],
				}}
				options={{
					indexAxis: "y",
					plugins: {
						legend: { display: false },
						tooltip: {
							callbacks: {
								label: (ctx) => `${ctx.parsed.x}%`,
							},
						},
						datalabels: false,
					},
					scales: {
						x: {
							max: 100,
							ticks: { callback: (v) => `${v}%` },
						},
						y: { grid: { display: false } },
					},
				}}
			/>
		</div>
	</div>
);
const StockOutReasonDonut = () => (
	<div className="bg-white rounded-xl shadow-sm p-4">
		<h3 className="text-sm font-medium text-gray-700 mb-2">
			Drug Stock-out Reasons (%)
		</h3>

		{/* Fixed height container */}
		<div className="h-[270px] flex items-center justify-center">
			<Doughnut
				data={{
					labels: [
						"Procurement Delay",
						"Distribution Delay",
						"Forecasting Error",
					],
					datasets: [
						{
							data: [46, 34, 20],
							backgroundColor: ["#f43f5e", "#f97316", "#6366f1"],
							borderWidth: 0,
						},
					],
				}}
				options={{
					maintainAspectRatio: false, // 🔑 IMPORTANT
					cutout: "60%",
					plugins: {
						legend: {
							position: "bottom",
							labels: {
								boxWidth: 10,
								padding: 10,
							},
						},
						tooltip: {
							callbacks: {
								label: (ctx) => `${ctx.label}: ${ctx.parsed}%`,
							},
						},
						datalabels: false,
					},
				}}
			/>
		</div>
	</div>
);

const PreventiveCoverageStack = () => {
	return (
		<div className="bg-white rounded-xl shadow-sm p-4">
			<h3 className="text-sm font-medium text-gray-700 mb-3">
				Preventive Therapy & Comorbidity Coverage (%)
			</h3>

			<Bar
				data={{
					labels: preventiveCoverageData.labels,
					datasets: [
						{
							label: "Covered",
							data: preventiveCoverageData.values,
							backgroundColor: "#34d399",
						},
						{
							label: "Coverage Gap",
							data: preventiveCoverageData.targets.map(
								(t, i) => t - preventiveCoverageData.values[i]
							),
							backgroundColor: "#e5e7eb",
						},
					],
				}}
				options={{
					indexAxis: "y",
					plugins: {
						legend: { position: "bottom" },
						tooltip: {
							callbacks: {
								label: (ctx) => `${ctx.parsed.x}%`,
							},
						},
						datalabels: false,
					},
					scales: {
						x: {
							stacked: true,
							max: 100,
							ticks: {
								callback: (v) => `${v}%`,
							},
							grid: { color: "#f1f5f9" },
						},
						y: {
							stacked: true,
							grid: { display: false },
						},
					},
				}}
			/>
		</div>
	);
};
const TbDiabetesBurdenBar = () => {
	return (
		<div className="bg-white rounded-xl shadow-sm p-4">
			<h3 className="text-sm font-medium text-gray-700 mb-3">
				TB–Diabetes Dual Burden
			</h3>

			<Bar
				data={{
					labels: [tbDiabetesBurden.label],
					datasets: [
						{
							data: [tbDiabetesBurden.value],
							backgroundColor: "#f97316",
							barThickness: 24,
							borderRadius: 6,
						},
					],
				}}
				options={{
					indexAxis: "y",
					plugins: {
						legend: { display: false },
						tooltip: {
							callbacks: {
								label: (ctx) => `${ctx.parsed.x}% of TB patients`,
							},
						},
						datalabels: false,
					},
					
					scales: {
						x: {
							max: 100,
							ticks: {
								callback: (v) => `${v}%`,
							},
							grid: { color: "#f1f5f9" },
						},
						y: {
							grid: { display: false },
						},
					},
				}}
			/>
		</div>
	);
};

const KpiCard = ({ title, value, unit, change, trendData }) => {
	const isNegative = change < 0;

	return (
		<div className="bg-white rounded-xl shadow-sm p-4">
			{/* Header */}
			<div className="flex items-center justify-between">
				<p className="text-sm text-gray-500">{title}</p>

				<span
					className={`text-xs font-medium px-2 py-0.5 rounded ${isNegative
						? "bg-rose-50 text-rose-600"
						: "bg-green-50 text-green-600"
						}`}
				>
					{change > 0 ? "+" : ""}
					{change}%
				</span>
			</div>

			{/* Value */}
			<div className="mt-1">
				<h3 className="text-3xl font-semibold text-gray-900">
					{value}
					<span className="text-base font-medium text-gray-500 ml-1">
						{unit}
					</span>
				</h3>
			</div>

			{/* Context */}
			<p className="text-xs text-gray-400 mt-0.5">
				Relative to last month
			</p>

			{/* Trend Chart */}
			<div className="mt-3 h-16">
				<Line
					data={{
						labels: trendData.map((_, i) => i),
						datasets: [
							{
								data: trendData,
								borderColor: isNegative ? "#f97316" : "#22c55e",
								backgroundColor: isNegative
									? "rgba(249,115,22,0.25)"
									: "rgba(34,197,94,0.25)",
								fill: true,
								tension: 0.4,
								pointRadius: 0,
								borderWidth: 2,
							},
						],
					}}
					options={{
						responsive: true,
						maintainAspectRatio: false,
						plugins: {
							legend: { display: false },
							tooltip: {
								enabled: true,
								backgroundColor: "rgba(17,24,39,0.9)",
								titleColor: "#fff",
								bodyColor: "#fff",
								padding: 8,
								displayColors: false,
								callbacks: {
									label: (ctx) => `${ctx.parsed.y}${unit}`,
								},
							},
							datalabels: false
						},
						scales: {
							x: { display: false },
							y: { display: false },
						},
					}}
				/>
			</div>
		</div>
	);
};

const TreatmentOutcomeStack = () => {
	return (
		<div className="bg-white rounded-xl shadow-sm p-4">
			<h3 className="text-sm font-medium text-gray-700 mb-3">
				Treatment Outcome Distribution (%)
			</h3>

			<Bar
				data={{
					labels: tbOutcomeData.labels,
					datasets: [
						{
							label: "Treatment Success",
							data: [tbOutcomeData.success],
							backgroundColor: "#22c55e",
						},
						{
							label: "Lost to Follow-up",
							data: [tbOutcomeData.ltfu],
							backgroundColor: "#f97316",
						},
						{
							label: "Death",
							data: [tbOutcomeData.death],
							backgroundColor: "#f43f5e",
						},
						{
							label: "Not Evaluated",
							data: [tbOutcomeData.notEvaluated],
							backgroundColor: "#94a3b8",
						},
					],
				}}
				options={{
					indexAxis: "y",
					responsive: true,
					plugins: {
						legend: { position: "bottom" },
						tooltip: {
							callbacks: {
								label: (ctx) => `${ctx.parsed.x}%`,
							},
						},
						datalabels: false
					},
					scales: {
						x: {
							stacked: true,
							max: 100,
							ticks: {
								callback: (v) => `${v}%`,
							},
							grid: { color: "#f1f5f9" },
						},
						y: {
							stacked: true,
							grid: { display: false },
						},
					},
				}}
			/>
		</div>
	);
};

const TreatmentTimelinessTrend = () => {
	return (
		<div className="bg-white rounded-xl shadow-sm p-4">
			<h3 className="text-sm font-medium text-gray-700 mb-3">
				Median Days to Treatment Initiation
			</h3>

			<Line
				data={{
					labels: treatmentDelayTrend.labels,
					datasets: [
						{
							label: "Actual",
							data: treatmentDelayTrend.actual,
							borderColor: "#6366f1",
							backgroundColor: "rgba(99,102,241,0.15)",
							tension: 0.4,
							fill: true,
						},
						{
							label: "Target (≤5 days)",
							data: treatmentDelayTrend.target,
							borderColor: "#94a3b8",
							borderDash: [6, 4],
							pointRadius: 0,
						},
					],
				}}
				options={{
					plugins: {
						legend: { position: "bottom" },
						tooltip: {
							callbacks: {
								label: (ctx) =>
									`${ctx.dataset.label}: ${ctx.parsed.y} days`,
							},
						},
					},
					scales: {
						y: {
							ticks: {
								callback: (v) => `${v} d`,
							},
							grid: { color: "#f1f5f9" },
						},
						x: {
							grid: { display: false },
						},
					},
				}}
			/>
		</div>
	);
};

/* ------------------------------------------------------------------ */
/* TB Care Cascade Component                                           */
/* ------------------------------------------------------------------ */
const TbCareCascade = () => {
	return (
		<div className="bg-white rounded-xl shadow-sm p-4">
			<h3 className="text-sm font-medium text-gray-700 mb-3">
				Patient Flow Across TB Care Pathway (%)
			</h3>

			<Bar
				data={{
					labels: tbCascadeData.labels,
					datasets: [
						{
							data: tbCascadeData.values,
							backgroundColor: [
								"#6366f1",
								"#60a5fa",
								"#34d399",
								"#fbbf24",
							],
							borderRadius: 6,
							barThickness: 26,
						},
					],
				}}
				options={{
					indexAxis: "y",
					plugins: {
						legend: { display: false },
						tooltip: {
							callbacks: {
								label: (ctx) => `${ctx.parsed.x}%`,
							},
						},
						datalabels: false,
					},
					scales: {
						x: {
							max: 100,
							ticks: {
								callback: (v) => `${v}%`,
							},
							grid: { color: "#f1f5f9" },
						},
						y: {
							grid: { display: false },
						},
					},
				}}
			/>
		</div>
	);
};

/* ------------------------------------------------------------------ */
/* Main Dashboard                                                      */
/* ------------------------------------------------------------------ */
export default function CommunicableDiseaseDashboard() {
	const handleMenuClick = () => {
		console.log("menu clicked (header)");
	};

	return (
		<div className="min-h-screen bg-gray-50">
			<Header
				onMenuClick={handleMenuClick}
				dashboardName="Communicable Disease Dashboard"
			/>

			<div className="mx-auto p-3 space-y-6">
				{/* Section 1 – KPIs */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2">
					{tbKpis.map((kpi, idx) => (
						<KpiCard key={idx} {...kpi} />
					))}
				</div>

				{/* Section 2 – Care Cascade */}
				<section className="space-y-4 mt-3">
					<div className="grid grid-cols-12 gap-2">
						<div className="col-span-12 lg:col-span-4">
							<h2 className="text-lg font-semibold text-gray-800">
								TB Care Cascade & Risk Gaps
							</h2>
						</div>
						<div className="col-span-12 lg:col-span-8">
							<h2 className="text-lg font-semibold text-gray-800">Treatment Outcomes & Quality of Care</h2>
						</div>

					</div>


					<div className="grid grid-cols-12 gap-2 mt-1">
						<div className="col-span-12 lg:col-span-4">
							<TbCareCascade />
						</div>
						{/* <div className="grid grid-cols-12 gap-2"> */}
						<div className="col-span-12 lg:col-span-4">
							<TreatmentOutcomeStack />
						</div>

						<div className="col-span-12 lg:col-span-4">
							<TreatmentTimelinessTrend />
						</div>
						{/* </div> */}
						{/* <div className="col-span-12 lg:col-span-4 space-y-4">
							<KpiCard {...ltfuKpi} />
							<KpiCard {...mdrKpi} />
							</div> */}
					</div>
					<section className="space-y-4">
						<h2 className="text-lg font-semibold text-gray-800">
							Preventive Therapy & Comorbidity Integration
						</h2>

						<div className="grid grid-cols-12 gap-2">
							<div className="col-span-12 lg:col-span-4">
								<PreventiveCoverageStack />
							</div>

							<div className="col-span-12 lg:col-span-4">
								<TbDiabetesBurdenBar />
							</div>
							<div className="grid grid-cols-1 lg:col-span-4 gap-2">
								<HivKpiCard
									title="HIV Prevalence Rate"
									value={hivData.prevalence}
									unit="%"
								/>
								<HivKpiCard
									title="ART Coverage"
									value={hivData.artCoverage}
									unit="%"
								/>
							</div>
						</div>
					</section>
					<section className="space-y-4">
						<h2 className="text-lg font-semibold text-gray-800">
							Patient Support, DBT & Community Engagement
						</h2>

						<div className="grid grid-cols-12 gap-2">
							<div className="col-span-12 lg:col-span-4">
								<DbtSupportChart />
							</div>

							<div className="col-span-12 lg:col-span-4">
								<NikshayMitraCoverage />
							</div>
							<div className="col-span-12 lg:col-span-4">
								<HivKpiCard
									title="HIV Prevalence Rate"
									value={hivData.prevalence}
									unit="%"
								/>
								<HivKpiCard
									title="ART Coverage"
									value={hivData.artCoverage}
									unit="%"
								/>
							</div>
						</div>
					</section>
					<section className="space-y-4">
						<h2 className="text-lg font-semibold text-gray-800">
							DR-TB, Adherence & Supply Chain Readiness
						</h2>

						<div className="grid grid-cols-12 gap-2">
							<div className="col-span-12 lg:col-span-4">
								<DrtbTreatmentStatusBar />
							</div>
							<div className="col-span-12 lg:col-span-4">
								<AdherenceMethodMixBar />
							</div>
							<div className="col-span-12 lg:col-span-4">
								<StockOutReasonDonut />
							</div>
						</div>
					</section>
					<section className="space-y-4">
						<h2 className="text-lg font-semibold text-gray-800">
							Vector-borne Diseases
						</h2>

						<div className="grid grid-cols-12  gap-2">
							<div className="col-span-12 lg:col-span-4">
								<VbdCaseBurdenChart />
							</div>

							<div className="col-span-12 lg:col-span-4">
								<VbdCfrTrendChart />
							</div>
							<div className="col-span-12 lg:col-span-4">
									<HepatitisTrendChart />
								</div>
						</div>
					</section>


					<section className="space-y-4">
						<h2 className="text-lg font-semibold text-gray-800">
							Executive Decision Board
						</h2>

						<OverallStatusBanner />

						<div className="grid grid-cols-12 gap-2">
							<div className="col-span-12 lg:col-span-7">
								<ProgramScorecard />
							</div>

							<div className="col-span-12 lg:col-span-5">
								<PriorityActions />
							</div>
						</div>
					</section>




				</section>
			</div>
		</div>
	);
}
